// PostDetail.js
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { vi } from "date-fns/locale";
import { ThumbsUp, MessageCircle, Share2, X, ChevronLeft, ChevronRight } from "lucide-react";
import PhoneLoginPopup from "../components/PhoneLoginPopup";
import SharePopup from "../components/SharePopup";
import { useSwipeable } from "react-swipeable";

export default function PostDetail() {
  const { postId, focus } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingPost, setLoadingPost] = useState(true);
  const [loadingComments, setLoadingComments] = useState(true);
  const [linkPreview, setLinkPreview] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeDocId, setLikeDocId] = useState(null);
  const [otherPosts, setOtherPosts] = useState([]);
  const scrollRef = useRef(null);
  const [showShare, setShowShare] = useState(false);
  const commentInputRef = useRef(null);

  // Tự động focus vào ô nhập bình luận khi mở màn hình
  useEffect(() => {
    if (focus === '1' && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [focus]);

  // Lấy danh sách 9 bài viết khác mới nhất
  useEffect(() => {
    const fetchOtherPosts = async () => {
      const q = query(
        collection(db, "posts"),
        orderBy("createdDate", "desc"),
        // lấy nhiều hơn 9 vì sẽ filter loại bài hiện tại
      );

      const snapshot = await getDocs(q);
      const list = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((p) => p.id !== postId)
        .slice(0, 9); // chỉ lấy 9 bài

      setOtherPosts(list);
    };

    fetchOtherPosts();
  }, [postId]);


  // Lấy thông tin post
  useEffect(() => {
    const fetchPost = async () => {
      setLoadingPost(true);
      const docRef = doc(db, "posts", postId);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setPost({ id: snapshot.id, ...snapshot.data() });
      }
      setLoadingPost(false);
    };
    fetchPost();
  }, [postId]);

  // Lấy thông tin like
  useEffect(() => {
    if (!currentUser) {
      setLiked(false);
      setLikeDocId(null);
      return;
    }
    const checkLike = async () => {
      const q = query(
        collection(db, "likes"),
        where("postId", "==", postId),
        where("userId", "==", currentUser.uid)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setLiked(true);
        setLikeDocId(snapshot.docs[0].id);
      } else {
        setLiked(false);
        setLikeDocId(null);
      }
    };
    checkLike();
  }, [currentUser, postId]);

  // Lắng nghe comment realtime
  useEffect(() => {
    const commentsRef = collection(db, "posts", postId, "comments");
    const q = query(commentsRef, orderBy("createdDate", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(list);
      setLoadingComments(false);
    });
    return () => unsubscribe();
  }, [postId]);

  useEffect(() => {
    async function fetchPreview() {
      if (post != null && post.linkContent) {
        try {
          const res = await fetch(
            `https://getlinkpreview-vbnbnaa5aq-uc.a.run.app?url=${encodeURIComponent(post.linkContent)}`
          );
          const data = await res.json();
          if (!data.error) setLinkPreview(data);
        } catch (err) {
          console.error("Failed to fetch link preview:", err);
        }
      }
    }
    fetchPreview();
  }, [post]);

  // Gửi comment
  const handleAddComment = async () => {
    if (!currentUser) {
      alert("Bạn cần đăng nhập để bình luận");
      return;
    }
    if (!newComment.trim()) return;

    const commentsRef = collection(db, "posts", postId, "comments");
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("phone", "==", currentUser.phoneNumber));
    const querySnapshot = await getDocs(q);

    var userData = {
      name: "Người dùng",
      avatarUrl: `https://ui-avatars.com/api/?name=User&background=random`
    };

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      userData = {
        name: data.name || "Người dùng",
        avatarUrl:
          data.avatarUrl ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name || "User")}&background=random`
      };
    }

    await addDoc(commentsRef, {
      userId: currentUser.uid,
      userName: userData.name,
      avatarUrl: userData.avatarUrl,
      content: newComment.trim(),
      createdDate: serverTimestamp(),
    });

    setNewComment("");
  };

  const handleLikeClick = async () => {
    if (!currentUser) {
      setShowLoginPopup(true);
      return;
    }

    if (liked && likeDocId) {
      // Bỏ quan tâm
      await deleteDoc(doc(db, "likes", likeDocId));
      setLiked(false);
      setLikeDocId(null);
    } else {
      // Quan tâm
      const docRef = await addDoc(collection(db, "likes"), {
        createdDate: Date.now(),
        id: "", // sẽ cập nhật sau
        postId: post.id,
        userId: currentUser.uid
      });
      await updateDoc(docRef, { id: docRef.id }); // hoặc updateDoc(docRef, { id: docRef.id })
      setLiked(true);
      setLikeDocId(docRef.id);
    }
  };

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };


  return (
    <div className="max-w-3xl mx-auto p-4">
      {/* Nút quay về */}
      <button
        onClick={() => navigate("/")}
        className="mb-4 flex items-center gap-2 text-vietnam-primary hover:underline"
      >
        ← Quay về trang chủ
      </button>

      {/* Tiêu đề & Nội dung bài viết */}
      <div className="bg-white p-3 md:p-4 rounded shadow text-sm md:text-base">
        {loadingPost ? (
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-2/3 mb-3"></div>
            <div className="h-64 bg-gray-300 rounded mb-3"></div>
            <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6"></div>
          </div>
        ) : (
          post && (
            <>
              <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-2">{post.title}</h3>

              {post.textContent && (
                <p className="mb-1 md:mb-2 whitespace-pre-line">{post.textContent}</p>
              )}

              <PostMedia mediaContent={post.mediaContent} />

              {linkPreview && (
                <a
                  href={linkPreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border rounded-lg overflow-hidden mt-3"
                >
                  {linkPreview.image && (
                    <img
                      src={linkPreview.image}
                      alt={linkPreview.title}
                      className="w-full object-cover h-48"
                    />
                  )}
                  <div className="p-3">
                    <h4 className="font-semibold">{linkPreview.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {linkPreview.description}
                    </p>
                  </div>
                </a>
              )}

              <p className="text-gray-500 text-xs md:text-sm mt-2">
                {'Admin đã đăng ' + formatDistanceToNow(new Date(post.createdDate), { addSuffix: true, locale: vi })}
              </p>
            </>
          )
        )}

        <div className="mt-3 md:mt-4 flex space-x-1 md:space-x-2 text-xs md:text-sm">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Ngăn click lan lên div cha
              handleLikeClick();
            }}
            className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 ${liked ? "bg-vietnam-light text-vietnam-primary" : "hover:bg-red-50"
              }`}
          >
            <ThumbsUp className={`w-4 h-4 ${liked ? "text-vietnam-primary" : "text-vietnam-dark"}`} />
            <span>{liked ? "Đã quan tâm" : "Quan tâm"}</span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation(); // Ngăn click lan lên div cha
              setShowShare(true);
            }}
            className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors duration-200">
            <Share2 className="w-4 h-4 text-vietnam-primary" />
            <span className="text-gray-700">Chia sẻ</span>
          </button>
        </div>

        {/* Các tin tức khác */}
        {otherPosts.length > 0 && (
          <div className="mt-6 border-t">
            <h2 className="font-semibold mt-6 text-base md:text-lg">Các tin tức khác</h2>

            <div className="relative">
              {/* Nút trái */}
              <button
                onClick={scrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow p-2 rounded-full"
              >
                <ChevronLeft size={20} />
              </button>

              {/* Scroll View ngang */}
              <div
                ref={scrollRef}
                className="flex overflow-x-auto no-scrollbar gap-3 px-8 py-2"
              >
                {otherPosts.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/post/${item.id}/0`)}
                    className="min-w-[200px] bg-white rounded-lg shadow cursor-pointer hover:shadow-md transition-all p-3"
                  >
                    {item.mediaContent?.[0]?.url && (
                      <img
                        src={item.mediaContent[0].thumbUrl}
                        alt={item.title}
                        className="h-28 w-full object-cover rounded mb-2"
                      />
                    )}
                    <p className="font-semibold text-sm line-clamp-2">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(item.createdDate), {
                        addSuffix: true,
                        locale: vi,
                      })}
                    </p>
                  </div>
                ))}
              </div>

              {/* Nút phải */}
              <button
                onClick={scrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow p-2 rounded-full"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}


        {/* Comment section */}
        <div className="mt-4 border-t pt-4">
          <h2 className="font-semibold mb-3">Bình luận</h2>

          {/* Input */}
          <div className="flex items-center gap-2 mb-4">
            <input
              ref={commentInputRef}
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Viết bình luận..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring focus:border-vietnam-primary"
            />
            <button
              onClick={handleAddComment}
              className="bg-vietnam-primary text-white px-4 py-2 rounded-lg hover:bg-vietnam-dark text-sm"
            >
              Gửi
            </button>
          </div>

          {/* Danh sách comment */}
          {loadingComments ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : comments.length === 0 ? (
            <p className="text-gray-500 text-sm">Chưa có bình luận nào</p>
          ) : (
            comments.map((cmt) => (
              <div key={cmt.id} className="flex items-center gap-3 mb-3">
                <img
                  src={cmt.avatarUrl}
                  alt={cmt.userName}
                  className="w-8 h-8 rounded-full"
                />
                <div className="bg-gray-100 rounded-lg px-3 py-2 flex-1">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium">{cmt.userName}</p>
                    {cmt.createdDate?.seconds && (
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(
                          new Date(cmt.createdDate.seconds * 1000),
                          { addSuffix: true, locale: vi }
                        )}
                      </span>
                    )}
                  </div>
                  <p className="text-sm">{cmt.content}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Login Popup */}
      {showLoginPopup && (
        <PhoneLoginPopup onClose={() => setShowLoginPopup(false)} />
      )}
      {/* Share Popup */}
      {showShare && (
        <SharePopup
          postId={post.id}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}

function PostMedia({ mediaContent }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handlers = useSwipeable({
    onSwipedLeft: () =>
      setSelectedIndex((prev) =>
        prev < (mediaContent?.length || 0) - 1 ? prev + 1 : 0
      ),
    onSwipedRight: () =>
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : (mediaContent?.length || 0) - 1
      ),
    trackMouse: true,
  });

  if (!Array.isArray(mediaContent) || mediaContent.length === 0) return null;

  return (
    <>
      {/* Hiển thị media trong post */}
      {mediaContent.map((media, index) =>
        media.isVideo ? (
          <video
            key={index}
            controls
            playsInline
            className="mb-2 w-full rounded cursor-pointer"
            onClick={() => setSelectedIndex(index)}
          >
            <source src={media.url} type="video/mp4" />
          </video>
        ) : (
          <img
            key={index}
            src={media.url}
            alt="media"
            className="mb-2 w-full rounded cursor-pointer"
            onClick={() => setSelectedIndex(index)}
          />
        )
      )}

      {/* Lightbox fullscreen */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
          {...handlers}
        >
          {/* Nút đóng */}
          <button
            onClick={() => setSelectedIndex(null)}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <X size={32} />
          </button>

          {/* Nút prev */}
          <button
            onClick={() =>
              setSelectedIndex((prev) =>
                prev > 0 ? prev - 1 : mediaContent.length - 1
              )
            }
            className="absolute left-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <ChevronLeft size={36} />
          </button>

          {/* Media đang chọn */}
          <div className="max-w-5xl max-h-[80vh] px-4">
            {mediaContent[selectedIndex].isVideo ? (
              <video
                src={mediaContent[selectedIndex].url}
                controls
                autoPlay
                className="max-h-[80vh] rounded"
              />
            ) : (
              <img
                src={mediaContent[selectedIndex].url}
                alt="full"
                className="max-h-[80vh] rounded"
              />
            )}
          </div>

          {/* Nút next */}
          <button
            onClick={() =>
              setSelectedIndex((prev) =>
                prev < mediaContent.length - 1 ? prev + 1 : 0
              )
            }
            className="absolute right-4 text-white p-2 rounded-full hover:bg-white/20"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </>
  );
}